'use client';

import React from 'react';
import {
  Users, Linkedin, CalendarCheck, AlertTriangle,
  TrendingUp, Clock, MessageSquare, Target
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// Bento grid plan: 8 cards → grid-cols-4
// Row 1: hero (Total Leads, spans 1 col tall-card) + LinkedIn Rate + Meeting Rate + Overdue Tasks
// Row 2: Intro Sent + TL Shared + Follow-up Pending + Opportunity Count
// Hero card is taller with sparkline visual

interface KPICard {
  id: string;
  label: string;
  value: string | number;
  subvalue?: string;
  trend?: { direction: 'up' | 'down' | 'neutral'; label: string };
  icon: React.ElementType;
  colorClass: string;
  bgClass: string;
  alert?: boolean;
  span?: string;
}

const kpiCards: KPICard[] = [
  {
    id: 'kpi-total-leads',
    label: 'Total Leads',
    value: '247',
    subvalue: 'NRA Show 2026',
    trend: { direction: 'up', label: '+23 this week' },
    icon: Users,
    colorClass: 'text-primary',
    bgClass: 'bg-primary/8',
    span: 'col-span-1 row-span-2',
  },
  {
    id: 'kpi-linkedin-rate',
    label: 'LinkedIn Accept Rate',
    value: '62%',
    subvalue: '47 of 76 accepted',
    trend: { direction: 'up', label: '+4% vs last event' },
    icon: Linkedin,
    colorClass: 'text-sky-600',
    bgClass: 'bg-sky-50',
  },
  {
    id: 'kpi-meeting-rate',
    label: 'Meeting Conversion',
    value: '44%',
    subvalue: '8 of 18 confirmed',
    trend: { direction: 'up', label: 'Target: 40%' },
    icon: CalendarCheck,
    colorClass: 'text-emerald-600',
    bgClass: 'bg-emerald-50',
  },
  {
    id: 'kpi-overdue',
    label: 'Overdue Tasks',
    value: '12',
    subvalue: '5 high priority',
    trend: { direction: 'down', label: 'Action needed' },
    icon: AlertTriangle,
    colorClass: 'text-red-600',
    bgClass: 'bg-red-50',
    alert: true,
  },
  {
    id: 'kpi-intro-sent',
    label: 'Intro Messages Sent',
    value: '89',
    subvalue: '36% of pipeline',
    trend: { direction: 'up', label: '+12 this week' },
    icon: MessageSquare,
    colorClass: 'text-indigo-600',
    bgClass: 'bg-indigo-50',
  },
  {
    id: 'kpi-tl-shared',
    label: 'Content Shared',
    value: '41',
    subvalue: '3 articles, 2 PDFs',
    trend: { direction: 'neutral', label: 'Last: yesterday' },
    icon: TrendingUp,
    colorClass: 'text-violet-600',
    bgClass: 'bg-violet-50',
  },
  {
    id: 'kpi-followup',
    label: 'Follow-up Pending',
    value: '34',
    subvalue: '8 post-event overdue',
    trend: { direction: 'down', label: 'Overdue: 8' },
    icon: Clock,
    colorClass: 'text-amber-600',
    bgClass: 'bg-amber-50',
    alert: true,
  },
  {
    id: 'kpi-opportunity',
    label: 'Opportunities Created',
    value: '6',
    subvalue: 'Est. $340K pipeline',
    trend: { direction: 'up', label: '+2 this month' },
    icon: Target,
    colorClass: 'text-orange-600',
    bgClass: 'bg-orange-50',
  },
];

// Mini sparkline data for hero card
const sparklineData = [18, 24, 31, 38, 52, 67, 84, 102, 118, 134, 156, 178, 198, 214, 247];

export default function KPIBentoGrid() {
  const heroCard = kpiCards[0];
  const restCards = kpiCards.slice(1);

  return (
    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Hero card — spans 2 rows */}
      <div
        className={`bg-white rounded-xl border border-border shadow-card p-5 flex flex-col justify-between row-span-2 ${heroCard.alert ? 'border-red-200 bg-red-50/30' : ''}`}
      >
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className={`w-9 h-9 rounded-lg ${heroCard.bgClass} flex items-center justify-center`}>
              <heroCard.icon size={18} className={heroCard.colorClass} strokeWidth={1.8} />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {heroCard.subvalue}
            </span>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            {heroCard.label}
          </p>
          <p className="text-5xl font-bold tabular-nums text-foreground tracking-tight">
            {heroCard.value}
          </p>
          <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
            <TrendingUp size={11} />
            {heroCard.trend?.label}
          </p>
        </div>
        {/* Mini sparkline */}
        <div className="mt-4">
          <p className="text-[10px] text-muted-foreground mb-2">Lead growth — last 15 days</p>
          <div className="flex items-end gap-0.5 h-12">
            {sparklineData.map((val, i) => (
              <div
                key={`spark-${i}`}
                className="flex-1 rounded-sm bg-primary/20 hover:bg-primary/40 transition-colors"
                style={{ height: `${(val / 247) * 100}%` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Rest of cards */}
      {restCards.map((card) => {
        const Icon = card.icon;
        const trendColor =
          card.trend?.direction === 'up' ?'text-emerald-600'
            : card.trend?.direction === 'down' ?'text-red-500' :'text-muted-foreground';
        const TrendIcon =
          card.trend?.direction === 'up'
            ? TrendingUp
            : card.trend?.direction === 'down'
            ? AlertTriangle
            : Clock;

        return (
          <div
            key={card.id}
            className={`bg-white rounded-xl border shadow-card p-4 flex flex-col gap-2 transition-shadow hover:shadow-card-hover ${
              card.alert ? 'border-red-200 bg-red-50/20' : 'border-border'
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {card.label}
              </p>
              <div className={`w-7 h-7 rounded-lg ${card.bgClass} flex items-center justify-center`}>
                <Icon size={14} className={card.colorClass} strokeWidth={1.8} />
              </div>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground tracking-tight">
              {card.value}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-muted-foreground">{card.subvalue}</p>
              <div className={`flex items-center gap-1 text-[10px] font-medium ${trendColor}`}>
                <TrendIcon size={10} />
                <span>{card.trend?.label}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}